import { useEffect } from 'react';

import { useIsFocused } from '@react-navigation/native';

import { URLS } from '@/data';
import { useIsAppActive } from '@/hooks/use-resume';
import { useIsCashCurrency } from '@/hooks/use-social-currency';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { JACKPOT_POOL_CURRENCY, getJackpotPoolAmount } from '../utils/utils';

export const JACKPOT_POOL_QUERY_KEY = ['casino-jackpot-pool'];
const JACKPOT_POOL_TYPE = 'real';
const JACKPOT_POOL_REFETCH_INTERVAL_MS = 1000;
const JACKPOT_POOL_TIMEOUT_MS = 10 * 1000;

type JackpotWin = {
    type: string;
    win: string;
};

export type JackpotPoolResponse = {
    type: 'real' | 'fun';
    timestamp: string;
    jackpots: Array<{
        currency: string;
        progressive_amount?: string;
        amount?: string;
        topWins: Array<JackpotWin>;
        latestWins: Array<JackpotWin>;
    }>;
};

const fetchJackpotPool = async (): Promise<JackpotPoolResponse> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), JACKPOT_POOL_TIMEOUT_MS);
    try {
        const response = await fetch(
            `${URLS.PLAYSON_JACKPOT_POOL_URL}&type=${JACKPOT_POOL_TYPE}&currencies=${JACKPOT_POOL_CURRENCY}`,
            { method: 'GET', signal: controller.signal }
        );
        if (!response.ok) {
            throw new Error(`Failed to fetch jackpot pool: ${response.status}`);
        }
        return await response.json();
    } finally {
        clearTimeout(timeoutId);
    }
};

type JackpotTickerStore = {
    mountedCount: number;
    registerTicker: () => () => void;
};

const useJackpotTickerStore = create<JackpotTickerStore>(set => ({
    mountedCount: 0,
    registerTicker: () => {
        set(state => ({ mountedCount: state.mountedCount + 1 }));
        return () => set(state => ({ mountedCount: state.mountedCount - 1 }));
    },
}));

export const useCasinoJackpotPoolPolling = () => {
    const isCashCurrency = useIsCashCurrency();
    const isFocused = useIsFocused();
    const hasJackpotTicker = useJackpotTickerStore(state => state.mountedCount > 0);
    const isAppActive = useIsAppActive();

    useQuery({
        queryKey: JACKPOT_POOL_QUERY_KEY,
        queryFn: fetchJackpotPool,
        enabled: hasJackpotTicker && isCashCurrency && isFocused && isAppActive,
        refetchInterval: JACKPOT_POOL_REFETCH_INTERVAL_MS,
        retry: false,
        notifyOnChangeProps: [],
    });
};

export const useCasinoJackpotAmount = () => {
    const isCashCurrency = useIsCashCurrency();
    const registerTicker = useJackpotTickerStore(state => state.registerTicker);

    useEffect(() => registerTicker(), [registerTicker]);

    const { data } = useQuery({
        queryKey: JACKPOT_POOL_QUERY_KEY,
        queryFn: fetchJackpotPool,
        select: getJackpotPoolAmount,
        enabled: false,
        notifyOnChangeProps: ['data'],
    });

    return isCashCurrency ? data : undefined;
};
